import React, { useEffect, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { createTestimonial, deleteTestimonial, fetchTestimonials, TestimonialRow, updateTestimonial } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";
import "./testimonials.css";

const TestimonialsPage: React.FC = () => {
  const [rows, setRows] = useState<TestimonialRow[]>([]);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<TestimonialRow | null>(null);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);

  const loadTestimonials = async () => {
    try {
      const data = await fetchTestimonials();
      setRows(data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch testimonials");
    }
  };

  useEffect(() => {
    loadTestimonials();
  }, []);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const openAddModal = () => {
    setEditing(null);
    setName("");
    setText("");
    setShowModal(true);
  };

  const openEditModal = (row: TestimonialRow) => {
    setEditing(row);
    setName(row.name || "");
    setText(row.text || "");
    setShowModal(true);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      alert("Name and testimonial text are required.");
      return;
    }

    setBusy(true);
    try {
      if (editing) {
        await updateTestimonial(editing.id, name.trim(), text.trim());
      } else {
        await createTestimonial(name.trim(), text.trim());
      }
      setShowModal(false);
      setEditing(null);
      setName("");
      setText("");
      await loadTestimonials();
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Failed to save testimonial");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await deleteTestimonial(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Failed to delete testimonial");
    }
  };

  return (
    <DashboardLayout>
      <ActionToast action={toastAction} />
      <h1 className="list-title">Testimonials</h1>
      <p className="list-subtitle">{rows.length} total testimonials</p>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="list-toolbar mb-3">
        <button type="button" className="btn btn-success" onClick={openAddModal}>
          <i className="fa-solid fa-plus me-2" />
          Add Testimonial
        </button>
      </div>
      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Text</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{row.name}</td>
                <td className="sf-testimonial-text-cell"><div className="sf-testimonial-preview">{row.text}</div></td>
                <td className="text-end">
                  <div className="action-icons justify-content-end">
                    <button type="button" className="icon-btn" title="Edit" onClick={() => openEditModal(row)}>
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-4 text-muted">
                  No testimonials found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="dash-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h5 className="dash-modal-title">{editing ? "Edit Testimonial" : "Add Testimonial"}</h5>
              <button type="button" className="dash-modal-close" onClick={() => setShowModal(false)}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <form onSubmit={onSubmit}>
              <div className="dash-modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter name..."
                  />
                </div>
                <div>
                  <label className="form-label">Testimonial</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Enter testimonial..."
                  />
                </div>
              </div>
              <div className="dash-modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={busy}>
                  {busy ? "Saving..." : editing ? "Save Changes" : "Add Testimonial"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default TestimonialsPage;
